import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './app';
import { ApiError } from './lib/api';
import { addTelemetrySink } from './lib/telemetry';
import { SessionProvider } from './features/auth/session';
import { ToastViewport } from './components/ui/toast';
import { TooltipProvider } from './components/ui/tooltip';
import './index.css';

/* ---------------------------------------------------------------------------
   The entry point.

   PROVIDER ORDER IS LOAD-BEARING, so it is worth saying why it is this way:

     QueryClientProvider   outermost of the app providers, because SessionProvider
                           calls `useQueryClient()` — it clears the cache when the
                           refresh family is gone, and a signed-out console that
                           still holds another expert's queue in cache is a leak.
     BrowserRouter         above SessionProvider, because the session's failure
                           path navigates.
     SessionProvider       restores the session from the HttpOnly refresh cookie
                           before any screen renders, so a reload does not flash
                           the sign-in page at someone who is already signed in.
     TooltipProvider       one Radix provider for the whole app; a per-tooltip
                           provider re-arms the shared open/close delay and makes
                           a dense table flicker.

   `ToastViewport` sits outside the router so a toast raised by a mutation that
   navigates — Request changes does exactly that — is not unmounted by the
   navigation that caused it.
--------------------------------------------------------------------------- */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // An authorisation decision is not a transient failure. A 403 on this
        // surface means the claim was released or the assignment was removed, a
        // 404 means the chat is not this expert's to read, and a 409 is a
        // deliberate refusal carrying detail in its body. Retrying any of them
        // just delays the correct screen — and retrying a 409 promotion refusal
        // would re-run a pre-flight the expert has not read yet.
        if (error instanceof ApiError) {
          if ([400, 401, 403, 404, 409, 422].includes(error.status)) return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    },
    // Nothing on this surface is safe to replay blind. A claim, a message, an
    // answer and a promotion are each either idempotent by key or a decision the
    // expert has to take again deliberately.
    mutations: { retry: false },
  },
});

// Where a Sentry client is wired in production, with the scrub deny-list applied
// in `beforeSend`. Until then the sink is left empty rather than pointed at a
// console: unmapped contract values are already shouted about in development by
// lib/telemetry.ts, and a production console log is a place a screenshot can
// reach.
addTelemetrySink(() => {});

const root = document.getElementById('root');
if (!root) throw new Error('index.html is missing #root');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <TooltipProvider delayDuration={200} skipDelayDuration={300}>
            <App />
          </TooltipProvider>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
    <ToastViewport />
  </StrictMode>,
);
